import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { PenTool, RotateCcw, Check, X } from "lucide-react";

/**
 * SignatureInput Component
 * Allows users to draw their signature on a canvas
 */
export default function SignatureInput({ value, onChange, error }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [context, setContext] = useState(null);

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      
      // Set canvas size
      canvas.width = canvas.offsetWidth;
      canvas.height = 200;
      
      // Configure context
      ctx.strokeStyle = "#1f2937";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      
      setContext(ctx);

      // Load existing signature if any
      if (value) {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0);
          setHasSignature(true);
        };
        img.src = value;
      }
    }
  }, [value]);

  const startDrawing = (e) => {
    if (!context) return;
    
    setIsDrawing(true);
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    
    context.beginPath();
    context.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || !context) return;
    
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    
    context.lineTo(x, y);
    context.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    
    setIsDrawing(false);
    
    if (hasSignature && canvasRef.current) {
      // Convert canvas to base64 and save
      const dataUrl = canvasRef.current.toDataURL("image/png");
      onChange(dataUrl);
    }
  };

  const clearSignature = () => {
    if (!context || !canvasRef.current) return;
    
    context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasSignature(false);
    onChange("");
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        {/* Canvas */}
        <canvas
          ref={canvasRef}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          className={`w-full h-[200px] bg-white rounded-lg border-2 cursor-crosshair transition-all ${
            error
              ? "border-red-300 bg-red-50"
              : hasSignature
              ? "border-green-300"
              : "border-gray-300 hover:border-orange-400"
          }`}
          style={{ touchAction: "none" }}
        />

        {/* Placeholder when empty */}
        {!hasSignature && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <PenTool className="w-8 h-8 text-gray-400 mb-2" />
            <p className="text-sm text-gray-500">Draw your signature here</p>
            <p className="text-xs text-gray-400 mt-1">Use your mouse or touch screen</p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center">
        <div className="text-xs text-gray-500">
          {hasSignature ? (
            <span className="flex items-center text-green-600">
              <Check className="w-3 h-3 mr-1" />
              Signature captured
            </span>
          ) : (
            <span>Sign above to continue</span>
          )}
        </div>
        
        {hasSignature && (
          <motion.button
            type="button"
            onClick={clearSignature}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center space-x-1 px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
          >
            <RotateCcw className="w-3 h-3" />
            <span>Clear</span>
          </motion.button>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
    </div>
  );
}
